import { CommonModule } from "@angular/common";
import { Component, NgZone, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Subscription, timer } from "rxjs";
import { switchMap } from "rxjs/operators";
import {
    PaymentApiService,
    PaymentStatusResponse,
} from "src/app/shared/services/payment-api.service";
import {
    PaymentNotification,
    SignalrService,
} from "src/app/shared/services/signalr.service";

/** Trạng thái hiển thị. `review` gộp UnderReview và Reversed: tiền đã trừ, đang đối soát. */
type PaymentView = "pending" | "success" | "failed" | "review";

/** Khoảng thời gian hỏi lại trạng thái khi chưa có kết quả. */
const POLL_INTERVAL_MS = 3000;

/**
 * VNPay gửi IPN gần như ngay lập tức, nhưng khi cổng bận có thể chậm hơn. Sau mốc này thì ngừng
 * hỏi và để khách tự kiểm tra lại trong lịch sử đơn hàng, thay vì quay vòng vô hạn.
 */
const POLL_TIMEOUT_MS = 3 * 60 * 1000;

@Component({
    selector: "app-payment-result",
    standalone: true,
    imports: [CommonModule, RouterModule, TranslateModule],
    templateUrl: "./payment-result.component.html",
    styleUrl: "./payment-result.component.scss",
})
export class PaymentResultComponent implements OnInit, OnDestroy {
    view: PaymentView = "pending";
    orderId!: string;
    orderCode?: string;
    transactionId?: string;
    message?: string;

    /** true khi đã hết thời gian chờ mà VNPay vẫn chưa báo kết quả. */
    timedOut = false;

    private subscriptions = new Subscription();
    private pollStartedAt = 0;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private ngZone: NgZone,
        private paymentApi: PaymentApiService,
        private signalrService: SignalrService,
        public translate: TranslateService
    ) {}

    ngOnInit(): void {
        this.trapBrowserBack();

        this.route.queryParams.subscribe((params) => {
            this.orderId = params["orderId"];
            this.orderCode = params["orderCode"];
            this.transactionId = params["transactionId"];
            this.message = params["message"];

            if (!this.orderId && !this.orderCode) {
                this.router.navigate(["/"]);
                return;
            }

            // Trạng thái trên URL chỉ là gợi ý ban đầu do Return URL của VNPay mang về. Return URL
            // không còn quyết định thành/bại - IPN mới là kênh cập nhật và có thể chưa về lúc này -
            // nên luôn hỏi lại backend thay vì tin vào query param.
            if (this.orderId) {
                this.resolveStatus();
            } else {
                this.view = this.route.snapshot.data["status"] ?? "pending";
            }
        });

        this.subscriptions.add(
            this.signalrService.paymentNotification$.subscribe(
                (notification: PaymentNotification | null) => {
                    if (!notification) return;

                    const isMatch =
                        notification.orderId === this.orderId ||
                        notification.orderCode === this.orderCode;

                    if (isMatch) {
                        this.applyNotification(notification);
                    }
                }
            )
        );
    }

    private resolveStatus(): void {
        this.pollStartedAt = Date.now();

        // Hỏi ngay một lần rồi mới lắng nghe realtime: nếu IPN đã về lúc khách còn đang ở cổng thanh
        // toán thì sự kiện SignalR đã bắn xong từ trước và sẽ không bắn lại.
        this.subscriptions.add(
            this.paymentApi.getStatus(this.orderId).subscribe({
                next: (status) => {
                    this.applyStatus(status);

                    if (!status.isCompleted) {
                        this.joinRealtimeGroup();
                        this.startPolling();
                    }
                },
                error: () => {
                    // Không kết luận thất bại chỉ vì một request lỗi - tiền của khách có thể đã trừ.
                    this.view = "pending";
                    this.joinRealtimeGroup();
                    this.startPolling();
                },
            })
        );
    }

    private startPolling(): void {
        this.subscriptions.add(
            timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS)
                .pipe(switchMap(() => this.paymentApi.getStatus(this.orderId)))
                .subscribe({
                    next: (status) => {
                        this.applyStatus(status);

                        if (status.isCompleted) {
                            this.stopWatching();
                        } else if (Date.now() - this.pollStartedAt > POLL_TIMEOUT_MS) {
                            this.timedOut = true;
                            this.stopWatching();
                        }
                    },
                    error: () => {
                        // Bỏ qua lỗi tạm thời, vòng sau hỏi lại.
                    },
                })
        );
    }

    private applyStatus(status: PaymentStatusResponse): void {
        this.orderCode = status.orderCode ?? this.orderCode;
        this.transactionId = status.transactionId ?? this.transactionId;

        switch (status.transactionStatus) {
            case "Success":
                this.view = "success";
                break;
            case "Failed":
            case "Expired":
                this.view = "failed";
                break;
            case "UnderReview":
            case "Reversed":
                this.view = "review";
                break;
            default:
                this.view = "pending";
        }
    }

    private applyNotification(notification: PaymentNotification): void {
        this.transactionId = notification.transactionId ?? this.transactionId;
        this.message = notification.message ?? this.message;

        this.view =
            notification.status === "Success"
                ? "success"
                : notification.status === "UnderReview"
                  ? "review"
                  : "failed";

        this.stopWatching();
    }

    private joinRealtimeGroup(): void {
        if (!this.orderId) return;

        this.signalrService.joinGroup(this.orderId).catch(() => {
            // Mất realtime thì vẫn còn polling, không cần báo lỗi cho khách.
        });
    }

    private stopWatching(): void {
        if (this.orderId) {
            this.signalrService.leaveGroup(this.orderId).catch(() => undefined);
        }
        this.subscriptions.unsubscribe();
        this.subscriptions = new Subscription();
    }

    goHome() {
        this.ngZone.run(() => {
            // replaceUrl: bấm Back từ trang chủ không được quay lại màn hình kết quả thanh toán.
            this.router.navigateByUrl("/", { replaceUrl: true });
        });
    }

    /**
     * Về danh sách đơn hàng của khách.
     *
     * KHÔNG phải "/order-details" - route đó là trang CHECKOUT (title "Thanh toán"), nên nút này
     * từng đưa khách vừa trả tiền xong quay lại form đặt hàng đã điền sẵn. Danh sách đơn thật nằm ở
     * tab order_history trong trang cá nhân, cùng địa chỉ mà email xác nhận đơn đang trỏ tới.
     *
     * replaceUrl để trang kết quả không nằm lại trong lịch sử: bấm Back từ danh sách đơn không được
     * quay về màn hình thanh toán nữa.
     */
    goToOrders() {
        this.ngZone.run(() => {
            this.router.navigate(["/profiles"], {
                queryParams: this.orderId
                    ? { tab: "order_history", orderId: this.orderId, view: "detail" }
                    : { tab: "order_history" },
                replaceUrl: true,
            });
        });
    }

    /**
     * Thử thanh toán lại cho CHÍNH đơn này.
     *
     * Backend sinh một lần thử mới với vnp_TxnRef riêng nên không tạo đơn trùng. Trước đây hàm này
     * điều hướng sang "/checkout" - một route không tồn tại trong app - nên nút "Thử lại" chỉ dẫn
     * khách tới trang trắng.
     */
    retrying = false;

    /** "Kiểm tra lại với VNPay" trên màn hình chờ: khách đã trả tiền nhưng IPN thất lạc. */
    rechecking = false;
    recheckMessageKey: string | null = null;

    recheckWithGateway(): void {
        if (!this.orderId || this.rechecking) return;

        this.rechecking = true;
        this.recheckMessageKey = null;
        this.paymentApi.syncStatus(this.orderId).subscribe({
            next: (result) => {
                this.rechecking = false;
                switch (result.outcome) {
                    case "Applied":
                    case "AlreadyPaid":
                        // Cổng đã kết luận và BE đã áp - đọc lại trạng thái chuẩn thay vì tự suy.
                        this.paymentApi.getStatus(this.orderId).subscribe({
                            next: (status) => {
                                this.applyStatus(status);
                                if (status.isCompleted) this.stopWatching();
                            },
                        });
                        break;
                    case "InProgress":
                    case "RateLimited":
                        this.recheckMessageKey = "common.PAYMENT.PENDING_RECHECK_INPROGRESS";
                        break;
                    case "NotFoundAtGateway":
                    case "NoPendingAttempt":
                        this.recheckMessageKey = "common.PAYMENT.PENDING_RECHECK_NOTFOUND";
                        break;
                    default:
                        this.recheckMessageKey = "common.PAYMENT.PENDING_RECHECK_ERROR";
                }
            },
            error: () => {
                this.rechecking = false;
                this.recheckMessageKey = "common.PAYMENT.PENDING_RECHECK_ERROR";
            },
        });
    }

    retryPayment(): void {
        if (!this.orderId || this.retrying) return;

        this.retrying = true;
        this.subscriptions.add(
            this.paymentApi.createPayment(this.orderId).subscribe({
                next: (res) => {
                    if (res?.success && res.paymentUrl) {
                        // Cùng tab, và replace() để trang kết quả cũ không nằm lại trong lịch sử.
                        window.location.replace(res.paymentUrl);
                        return;
                    }

                    this.retrying = false;
                    this.message = res?.message || this.translate.instant("common.PAYMENT.RETRY_FAILED");
                },
                error: () => {
                    this.retrying = false;
                    this.message = this.translate.instant("common.PAYMENT.RETRY_FAILED");
                },
            })
        );
    }
    get canRetry(): boolean {
        return !!this.orderId;
    }

    /**
     * The browser history behind this page is VNPay's gateway (and the bank's OTP pages). "Back" would land
     * the customer on VNPay's "order does not exist or was already processed" error, so an extra history
     * entry is pushed here and the first Back is redirected to the home page instead.
     */
    private trapBrowserBack(): void {
        window.history.pushState({ paymentResult: true }, "", window.location.href);
        window.addEventListener("popstate", this.onPopState);
    }

    private readonly onPopState = (): void => {
        window.removeEventListener("popstate", this.onPopState);
        this.router.navigateByUrl("/", { replaceUrl: true });
    };

    ngOnDestroy(): void {
        window.removeEventListener("popstate", this.onPopState);
        this.signalrService.clearNotification();
        this.stopWatching();
    }
}
